// src/pages/MissingPaymentsReport.js
import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// 👇 Adjust this if you have a different env variable
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://157.180.43.56:5050'

function MissingPaymentsReport() {
  const [vehicles, setVehicles] = useState([])
  const [reportData, setReportData] = useState([])
  const [selectedVehicleId, setSelectedVehicleId] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [showAll, setShowAll] = useState(false)
  const [selectedDates, setSelectedDates] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // ---- small helpers ----
  const num = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }
  const fmt = (v) => num(v).toLocaleString()

  const buildRowsFromApi = (apiVehicles, allVehicles) => {
    const vehMap = new Map((allVehicles || []).map((v) => [String(v.id), v]))

    return (apiVehicles || []).map((item) => {
      const meta = vehMap.get(String(item.vehicle_id)) || {}

      const dailyRate = num(item.daily_rate)
      const contractStart = meta.contract_start
        ? String(meta.contract_start).slice(0, 10)
        : ''

      return {
        vehicle_id: item.vehicle_id,
        plate_number: item.plate_number || meta.plate_number || '',
        vehicle_type: meta.vehicle_type || '',
        driver_name: item.driver_name || meta.driver_name || '',
        daily_rate: dailyRate,
        contract_start: contractStart,
        paid_days: item.paidDaysCount ?? 0,
        total_unpaid_days: item.totalMissingDays ?? 0,
        total_missing_amount: item.expectedMissingAmount ?? 0,
        missing_dates: (item.missingDates || []).join(', '),
        total_paid: item.totalPaid ?? 0, // kept for future use if needed
      }
    })
  }

  const fetchVehicles = async () => {
    const res = await fetch(`${API_BASE_URL}/api/vehicles`)
    if (!res.ok) {
      throw new Error(`Failed to load vehicles: ${res.status}`)
    }
    const data = await res.json()
    return data.vehicles || []
  }

  const fetchMissingReport = async () => {
    const params = new URLSearchParams()
    if (fromDate) params.append('startDate', fromDate) // backend will fallback if not provided
    if (toDate) params.append('endDate', toDate)

    const url = `${API_BASE_URL}/api/reports/missing-payments${
      params.toString() ? `?${params.toString()}` : ''
    }`

    const res = await fetch(url)
    if (!res.ok) {
      throw new Error(`Failed to load missing payments: ${res.status}`)
    }
    const data = await res.json()
    return data.vehicles || []
  }

  const generateReport = async () => {
    try {
      setIsLoading(true)

      // 1) Ensure we have vehicles
      let currentVehicles = vehicles
      if (!currentVehicles || currentVehicles.length === 0) {
        currentVehicles = await fetchVehicles()
        setVehicles(currentVehicles)
      }

      // 2) Fetch missing-payments summary from backend
      const apiVehicles = await fetchMissingReport()

      // 3) Build frontend rows by joining with vehicle meta
      const rows = buildRowsFromApi(apiVehicles, currentVehicles)

      setReportData(rows)
    } catch (err) {
      console.error('[MissingPayments] Error generating report:', err)
      setReportData([])
    } finally {
      setIsLoading(false)
    }
  }

  // ✅ Auto-load on mount so dropdown + report have data immediately
  useEffect(() => {
    generateReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filtered rows (respect vehicle filter + Show All)
  const getFilteredRows = () => {
    return (reportData || [])
      .filter(
        (row) =>
          !selectedVehicleId ||
          String(row.vehicle_id) === String(selectedVehicleId)
      )
      .filter((row) => showAll || row.total_unpaid_days > 0)
  }

  const filteredRows = getFilteredRows()

  // Totals for footer
  const totals = filteredRows.reduce(
    (acc, row) => {
      acc.unpaidDays += num(row.total_unpaid_days)
      acc.missingAmount += num(row.total_missing_amount)
      return acc
    },
    { unpaidDays: 0, missingAmount: 0 }
  )

  const exportToExcel = () => {
    const rows = filteredRows
    if (rows.length === 0) return

    const exportRows = rows.map((item) => ({
      Plate: item.plate_number,
      Type: item.vehicle_type,
      Driver: item.driver_name,
      'Daily Rate': num(item.daily_rate),
      'Contract Start': item.contract_start,
      'Paid Days': item.paid_days,
      'Unpaid Days': item.total_unpaid_days,
      'Missing Amount (TZS)': num(item.total_missing_amount),
      'Missing Dates': item.missing_dates,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportRows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missing Payments')

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    const fileName = `missing_payments_${fromDate || 'start'}_${
      toDate || 'today'
    }_${stamp}.xlsx`

    XLSX.writeFile(workbook, fileName)
  }

  const exportToPDF = () => {
    const rows = filteredRows
    if (rows.length === 0) return

    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    const title = 'Missing Payments Report'
    const range = `Range: ${fromDate || '2000-01-01'} — ${toDate || 'Today'}`
    const stamp = `Generated: ${new Date().toLocaleString()}`

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(18)
    doc.text(title, 40, 50)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    doc.text(range, 40, 70)
    doc.text(stamp, 40, 86)

    autoTable(doc, {
      startY: 105,
      head: [
        [
          'Plate',
          'Type',
          'Driver',
          'Rate',
          'Start',
          'Paid',
          'Unpaid',
          'Missing Amount',
          'Missing Dates',
        ],
      ],
      body: rows.map((item) => [
        item.plate_number,
        item.vehicle_type,
        item.driver_name,
        fmt(item.daily_rate),
        item.contract_start,
        item.paid_days,
        item.total_unpaid_days,
        fmt(item.total_missing_amount),
        item.missing_dates,
      ]),
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [33, 33, 33],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 60 }, // Plate
        1: { cellWidth: 50 }, // Type
        2: { cellWidth: 70 }, // Driver
        8: { cellWidth: 180 }, // Missing Dates
      },
      margin: { left: 40, right: 40 },
    })

    // Footer totals row in PDF
    const finalY = doc.lastAutoTable.finalY || 105
    doc.setFont('helvetica', 'bold')
    doc.text(
      `Totals  •  Unpaid Days: ${fmt(
        totals.unpaidDays
      )}  •  Missing Amount: ${fmt(totals.missingAmount)} TZS`,
      40,
      finalY + 24
    )

    const fileName = `missing_payments_${fromDate || 'start'}_${
      toDate || 'today'
    }.pdf`
    doc.save(fileName)
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        📋 Missing Payments Report
      </Typography>

      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md={3}>
          <Select
            fullWidth
            value={selectedVehicleId}
            onChange={(e) => setSelectedVehicleId(String(e.target.value))}
            displayEmpty
          >
            <MenuItem value="">All Vehicles</MenuItem>
            {vehicles.map((v) => (
              <MenuItem key={v.id} value={String(v.id)}>
                {v.plate_number}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            type="date"
            label="From"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            type="date"
            label="To"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showAll}
                onChange={() => setShowAll(!showAll)}
              />
            }
            label="Show All"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Button
            variant="contained"
            onClick={generateReport}
            fullWidth
            disabled={isLoading}
          >
            🔄 {isLoading ? 'Loading...' : 'Generate Report'}
          </Button>
        </Grid>

        <Grid item xs={6} md={2}>
          <Button
            variant="outlined"
            onClick={exportToExcel}
            fullWidth
            disabled={filteredRows.length === 0}
          >
            📤 Excel
          </Button>
        </Grid>
        <Grid item xs={6} md={2}>
          <Button
            variant="outlined"
            onClick={exportToPDF}
            fullWidth
            disabled={filteredRows.length === 0}
          >
            📄 PDF
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Plate</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell align="right">Daily Rate</TableCell>
              <TableCell>Contract Start</TableCell>
              <TableCell align="right">Paid Days</TableCell>
              <TableCell align="right">Unpaid Days</TableCell>
              <TableCell align="right">Missing Amount</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No data
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.plate_number}</TableCell>
                  <TableCell>{row.vehicle_type}</TableCell>
                  <TableCell>{row.driver_name}</TableCell>
                  <TableCell align="right">{fmt(row.daily_rate)}</TableCell>
                  <TableCell>{row.contract_start}</TableCell>
                  <TableCell align="right">{row.paid_days}</TableCell>
                  <TableCell align="right">
                    {row.total_unpaid_days}
                  </TableCell>
                  <TableCell align="right">
                    {fmt(row.total_missing_amount)} TZS
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => setSelectedDates(row.missing_dates)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 700 }}>Totals</TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmt(totals.unpaidDays)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmt(totals.missingAmount)} TZS
              </TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {/* Missing Dates Modal */}
      <Dialog
        open={!!selectedDates}
        onClose={() => setSelectedDates(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Missing Dates</DialogTitle>
        <DialogContent>
          <Typography whiteSpace="pre-wrap">{selectedDates}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default MissingPaymentsReport